<script setup lang="ts">
import device from '@@/bits/device'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzOverlay from '@@/library/v4/components/OzOverlay.vue'
import type { VueCssClass } from '@@/types'
import { useScroll } from '@vueuse/core'
import { computed, ref, useSlots } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     *  TailwindCSS width class.
     *
     *  - `w-99` is for whiteboard card templates panel
     *  - `w-100` is for surface panels when on native tablet
     *  - `w-120` is for surface panels
     *  - `w-135` is for dashboard layout picker
     */
    widthClass?: 'w-99' | 'w-100' | 'w-120' | 'w-135'
    /**
     * Extra TailwindCSS classes for the modal container.
     */
    containerClasses?: VueCssClass
    darkMode?: boolean | 'auto'
    /**
     * Determines whether the modal should have a rounded top inner corner
     */
    topInnerCornerRadius?: 0 | 12 | 24
    /**
     * Determines whether the modal should have a rounded bottom inner corner
     */
    bottomInnerCornerRadius?: 0 | 12 | 24
    /**
     *  TailwindCSS z-index class.
     *
     *  - `z-modal2` is for all dialogs
     *  - `z-sidepanel` is for surface sidepanels
     */
    zIndexClass?: 'z-modal2' | 'z-sidepanel'
    isBodyScrollable?: boolean
    hideScrollbar?: boolean
    /**
     * Determines whether the scrollbar should overlay on top of content,
     * hideScrollbar must be false for this to work.
     * This is only supported on Chrome.
     */
    scrollbarOverlay?: boolean
    /**
     * Determines whether the scrollbar should have a gutter (always preserve
     * space for the scrollbar, even when it's not visible).
     */
    scrollbarGutterStable?: boolean
    /**
     * Determines which direction the OzSideModal should slide in
     */
    slideDirection?: OzSideModalSlideDirection
    /**
     * Determines CSS position
     */
    position?: 'fixed' | 'absolute'
    /**
     * Determines whether the scrim should be shown
     */
    xScrim?: boolean

    /**
     * Determines whether the divider should be shown
     */
    headerDividerMode?: 'always' | 'on-scroll'
  }>(),
  {
    widthClass: 'w-120',
    containerClasses: undefined,
    darkMode: 'auto',
    topInnerCornerRadius: 12,
    bottomInnerCornerRadius: 0,
    zIndexClass: undefined,
    isBodyScrollable: true,
    hideScrollbar: false,
    scrollbarOverlay: false,
    scrollbarGutterStable: false,
    slideDirection: OzSideModalSlideDirection.EndToStart,
    position: 'fixed',
    xScrim: true,
    headerDividerMode: 'on-scroll',
  },
)

const slots = useSlots()

const scrollingContainer = ref<HTMLElement>()
const { y: scrollTop } = useScroll(scrollingContainer)

const xHeaderDivider = computed(() => {
  if (props.headerDividerMode === 'always') return true
  if (props.headerDividerMode === 'on-scroll' && scrollTop.value > 0) return true
  return false
})

const scrollbarStyles = computed(() => {
  const defaultScrollbarStyles = [
    {
      'overflow-auto': props.isBodyScrollable,
      'h-full': !props.isBodyScrollable,
    },
    {
      'scrollbar-regular dark:scrollbar-regular-dark': props.darkMode === 'auto',
      'scrollbar-regular': props.darkMode === false,
      'dark:scrollbar-regular-dark': props.darkMode === true,
    },
    props.scrollbarGutterStable && 'scrollbar-gutter-stable',
  ]
  if (props.hideScrollbar) {
    return 'no-scrollbar overflow-auto'
  } else if (props.scrollbarOverlay && device.chrome) {
    return ['scrollbar-overlay', ...defaultScrollbarStyles]
  } else {
    return defaultScrollbarStyles
  }
})

const isTransitioningToEnter = ref(true)
const emit = defineEmits(['scrim-click', 'scrim-esc', 'transition:after-enter'])

const handleAfterEnterTransition = (event) => {
  isTransitioningToEnter.value = false
  emit('transition:after-enter', event)
}

const handleScrimClick = () => {
  if (isTransitioningToEnter.value) return
  emit('scrim-click')
}
</script>

<script lang="ts">
export enum OzSideModalSlideDirection {
  EndToStart = 'end-to-start',
  StartToEnd = 'start-to-end',
}

export default {}
</script>

<template>
  <Transition name="slide" appear @after-enter="handleAfterEnterTransition">
    <OzOverlay
      role="dialog"
      :should-fade-in="false"
      :z-index-class="zIndexClass"
      :dark-mode="darkMode"
      :position="position"
      :scrim="xScrim ? 'modal' : null"
      @scrim-click="handleScrimClick"
      @scrim-esc="$emit('scrim-esc')"
    >
      <div
        :class="[
          'modal-container',
          // Slide direction
          {
            'start-to-end': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'end-to-start': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Positioning
          position === 'fixed' ? 'fixed' : 'absolute',
          'inset-y-0',
          // Position after sliding
          {
            'start-0': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'end-0': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Layout
          widthClass,
          'flex',
          'flex-col',
          'overflow-hidden',
          topInnerCornerRadius === 12 && {
            'rounded-se-xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-ss-xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          bottomInnerCornerRadius === 12 && {
            'rounded-ee-xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-es-xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          topInnerCornerRadius === 24 && {
            'rounded-se-3xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-ss-3xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          bottomInnerCornerRadius === 24 && {
            'rounded-ee-3xl': slideDirection === OzSideModalSlideDirection.StartToEnd,
            'rounded-es-3xl': slideDirection === OzSideModalSlideDirection.EndToStart,
          },
          // Shadow
          'shadow-elevation-3',
          // Background
          {
            'bg-light-ui-100 dark:bg-dark-ui-100': darkMode === 'auto',
            'bg-light-ui-100': darkMode === false,
            'bg-dark-ui-100': darkMode === true,
          },
          containerClasses,
        ]"
      >
        <div v-if="slots.header" :class="[xHeaderDivider ? 'relative' : '']">
          <slot name="header" :scroll-top="scrollTop" />
          <OzDivider
            v-if="xHeaderDivider"
            class="absolute inset-x-0 bottom-0 mx-4"
            :color-scheme="OzDividerColorScheme.Solid"
          />
        </div>
        <div ref="scrollingContainer" :class="scrollbarStyles">
          <slot name="body" />
        </div>
      </div>
    </OzOverlay>
  </Transition>
</template>

<style lang="postcss" scoped>
.slide-enter-active,
.slide-enter-active > .modal-container {
  transition: all 0.3s cubic-bezier(0, 0, 0.2, 1);

  @media (prefers-reduced-motion) {
    transition-duration: 0s;
  }
}

.slide-leave-active,
.slide-leave-active > .modal-container {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);

  @media (prefers-reduced-motion) {
    transition-duration: 0s;
  }
}

.slide-enter,
.slide-leave-to {
  opacity: 0;
}

.slide-enter > .modal-container,
.slide-leave-to > .modal-container {
  &.end-to-start {
    transform: translateX(100%);

    [dir='rtl'] & {
      transform: translateX(-100%);
    }
  }

  &.start-to-end {
    transform: translateX(-100%);

    [dir='rtl'] & {
      transform: translateX(100%);
    }
  }
}
</style>
